import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

interface Options {
  /**
   * Called when the user presses Escape while focus is inside the trap.
   * Most callers wire this to the same handler that closes the modal.
   */
  onEscape?: () => void;
  /**
   * Another dialog is open on top of this one: Escape and Tab are left to it.
   * The listener is on the document, so without this both dialogs would answer
   * the same key — Escape would close the one underneath too.
   */
  paused?: boolean;
}

/**
 * Traps Tab focus inside `containerRef` and restores focus to the previously
 * focused element on unmount. Used by modal dialogs so keyboard users can't
 * accidentally tab into the background page.
 *
 * Tab on the last focusable wraps to the first; Shift+Tab on the first wraps
 * to the last. Hidden elements (display:none, the `hidden` attribute) are
 * skipped via a `getClientRects()` visibility check.
 */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement | null>,
  options: Options = {},
): void {
  // Keep the latest onEscape in a ref so we don't re-bind the keydown listener
  // every render — callers commonly pass an inline arrow function.
  const onEscapeRef = useRef(options.onEscape);
  onEscapeRef.current = options.onEscape;
  const pausedRef = useRef(!!options.paused);
  pausedRef.current = !!options.paused;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusables = (): HTMLElement[] =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        .filter((el) => el.getClientRects().length > 0);

    const initial = getFocusables();
    if (initial.length > 0) {
      initial[0].focus();
    } else {
      // Nothing to focus — make the container itself focusable so the next Tab
      // wraps inside instead of escaping to the background.
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      container.focus();
    }

    const handleKey = (e: KeyboardEvent) => {
      if (pausedRef.current) return;
      if (e.key === 'Escape') {
        if (onEscapeRef.current) {
          e.preventDefault();
          onEscapeRef.current();
        }
        return;
      }
      if (e.key !== 'Tab') return;

      const els = getFocusables();
      if (els.length === 0) {
        e.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const insideContainer = active != null && container.contains(active);

      if (e.shiftKey) {
        if (!insideContainer || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (!insideContainer || active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [containerRef]);
}
